/**
 * UserProfilePage — `/security/users/:id`.
 *
 * Displays a user's profile from service-identity, including their current
 * roles. The operator can assign or revoke roles from this page.
 *
 * Wired to real endpoints:
 *   GET  /v1/identity/users/:id          (Identity/readUser)
 *   GET  /v1/identity/roles              (Identity/readRole)
 *   POST /v1/identity/users/:id/roles    (Identity/grantRole)
 *   DELETE /v1/identity/users/:id/roles/:roleId (Identity/revokeRole)
 */
import { type JSX, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Shield, ShieldCheck, Trash2 } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  disableUser,
  enableUser,
  getUser,
  grantRole,
  listRoles,
  listUserRoles,
  revokeRole,
} from '../../lib/api/identity-admin';
import { useAuth } from '../../lib/auth/useAuth';
import { decodeStaffClaims } from '../../lib/auth/jwt';
import { PageHeader, StatusBadge } from '../shared/StaffUI';

export function UserProfilePage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const claims = decodeStaffClaims(token);
  const queryClient = useQueryClient();

  const viewingSelf = !id || id === claims?.subject || id === 'me';
  const userId = viewingSelf ? (claims?.subject ?? 'me') : (id ?? 'me');

  const [selectedRoleId, setSelectedRoleId] = useState('');

  const user = useQuery({
    queryKey: ['identity', 'users', userId],
    queryFn: () => getUser(userId),
    retry: false,
  });

  const roles = useQuery({
    queryKey: ['identity', 'roles'],
    queryFn: listRoles,
    retry: false,
  });

  const userRoles = useQuery({
    queryKey: ['identity', 'users', userId, 'roles'],
    queryFn: () => listUserRoles(userId),
    retry: false,
  });

  const grantMutation = useMutation({
    mutationFn: () => grantRole(userId, selectedRoleId),
    onSuccess: () => {
      setSelectedRoleId('');
      void queryClient.invalidateQueries({ queryKey: ['identity', 'users', userId] });
      void queryClient.invalidateQueries({ queryKey: ['identity', 'users', userId, 'roles'] });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (roleId: string) => revokeRole(userId, roleId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['identity', 'users', userId] });
      void queryClient.invalidateQueries({ queryKey: ['identity', 'users', userId, 'roles'] });
    },
  });

  const toggleStatus = useMutation({
    mutationFn: () =>
      user.data?.status === 'ACTIVE' ? disableUser(userId) : enableUser(userId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['identity', 'users', userId] });
      void queryClient.invalidateQueries({ queryKey: ['identity', 'users'] });
    },
  });

  const denied =
    user.isError &&
    user.error instanceof ApiError &&
    (user.error.status === 401 || user.error.status === 403);

  const displayName =
    user.data?.displayName ??
    (viewingSelf ? (claims?.displayName ?? 'Operator') : (id ?? ''));
  const initials = displayName
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'O';

  return (
    <div className="space-y-6">
      <PageHeader
        title={viewingSelf ? 'My profile' : 'User profile'}
        description="Account profile and role assignments."
      />

      {denied ? (
        <div
          data-testid="permission-required"
          className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800"
        >
          <code className="font-mono">Identity/readUser</code> permission required.
        </div>
      ) : user.isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          Identity service temporarily unavailable: {(user.error as Error).message}
        </div>
      ) : null}

      {/* Profile card */}
      <div
        data-testid="user-profile-card"
        className="bg-white border border-border rounded-xl p-5 space-y-4"
      >
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-nipost-green flex items-center justify-center text-white text-lg font-semibold">
            {initials}
          </div>
          <div>
            <p className="text-base font-semibold text-gray-900">
              {user.isPending ? 'Loading…' : displayName}
            </p>
            {user.data ? (
              <p className="text-xs text-muted font-mono">{user.data.email}</p>
            ) : null}
            {user.data?.id ? (
              <p className="text-[10px] font-mono text-muted mt-0.5">{user.data.id}</p>
            ) : null}
          </div>
          {user.data ? (
            <div className="ml-auto flex items-center gap-3">
              <StatusBadge status={user.data.status} />
              {!viewingSelf ? (
                <button
                  type="button"
                  data-testid={
                    user.data.status === 'ACTIVE'
                      ? `disable-user-${userId}`
                      : `enable-user-${userId}`
                  }
                  onClick={() => toggleStatus.mutate()}
                  disabled={toggleStatus.isPending}
                  className={`px-3 py-1.5 text-xs font-semibold rounded border disabled:opacity-50 ${
                    user.data.status === 'ACTIVE'
                      ? 'border-red-300 text-red-700 hover:bg-red-50'
                      : 'border-nipost-green text-nipost-green hover:bg-nipost-green-light'
                  }`}
                >
                  {toggleStatus.isPending
                    ? 'Updating…'
                    : user.data.status === 'ACTIVE'
                      ? 'Disable account'
                      : 'Enable account'}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        {user.data ? (
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <dt className="text-[10px] uppercase tracking-wider text-muted mb-1">Phone</dt>
              <dd className="text-gray-900">{user.data.phone ?? '—'}</dd>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <dt className="text-[10px] uppercase tracking-wider text-muted mb-1">Operator Id</dt>
              <dd className="text-gray-900">{user.data.operatorId}</dd>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <dt className="text-[10px] uppercase tracking-wider text-muted mb-1">Platform admin</dt>
              <dd className="text-gray-900">{user.data.isPlatformAdmin ? 'Yes' : 'No'}</dd>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <dt className="text-[10px] uppercase tracking-wider text-muted mb-1">Created</dt>
              <dd className="text-gray-900">
                {new Date(user.data.createdAt).toLocaleDateString('en-GB')}
              </dd>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <dt className="text-[10px] uppercase tracking-wider text-muted mb-1">Last login</dt>
              <dd className="text-gray-900">
                {user.data.lastLoginAt
                  ? new Date(user.data.lastLoginAt).toLocaleDateString('en-GB')
                  : '—'}
              </dd>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <dt className="text-[10px] uppercase tracking-wider text-muted mb-1">
                Password changed
              </dt>
              <dd className="text-gray-900">
                {user.data.passwordChangedAt
                  ? new Date(user.data.passwordChangedAt).toLocaleDateString('en-GB')
                  : '—'}
              </dd>
            </div>
          </dl>
        ) : null}
      </div>

      {/* Role assignment panel */}
      {!viewingSelf ? (
        <div
          data-testid="role-assignment-panel"
          className="bg-white border border-border rounded-xl p-5 space-y-4"
        >
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-nipost-green" />
            <h3 className="text-sm font-semibold text-gray-900">Role assignment</h3>
          </div>
          <p className="text-xs text-muted">
            Assign or revoke roles. Changes take effect on next login (sessions are
            invalidated on role mutation to enforce fresh claims).
          </p>

          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-muted mb-1">Grant role</label>
              <select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm"
                aria-label="Role to grant"
                data-testid="role-select"
              >
                <option value="">— Select role —</option>
                {(roles.data ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.id})
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              data-testid="grant-role-btn"
              onClick={() => grantMutation.mutate()}
              disabled={!selectedRoleId || grantMutation.isPending}
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              {grantMutation.isPending ? 'Granting…' : 'Grant'}
            </button>
          </div>

          {grantMutation.isError ? (
            <p className="text-sm text-danger">{(grantMutation.error as Error).message}</p>
          ) : grantMutation.isSuccess ? (
            <p className="text-sm text-nipost-green" data-testid="grant-success">
              Role granted. User must re-login for new permissions.
            </p>
          ) : null}

          {/* Currently-granted roles with per-row revoke. */}
          <div className="border-t border-border pt-3" data-testid="user-roles-list">
            <p className="text-xs font-medium text-muted mb-2">Current roles</p>
            {userRoles.isPending ? (
              <p className="text-xs text-muted">Loading roles…</p>
            ) : userRoles.isError ? (
              <p className="text-xs text-danger">
                {(userRoles.error as Error).message}
              </p>
            ) : (userRoles.data?.length ?? 0) === 0 ? (
              <p className="text-xs text-muted" data-testid="no-roles">
                No roles granted.
              </p>
            ) : (
              <ul className="divide-y divide-border">
                {(userRoles.data ?? []).map((r) => (
                  <li
                    key={r.roleId}
                    data-testid={`granted-role-${r.roleId}`}
                    className="flex items-center justify-between py-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-3 h-3 text-nipost-green" />
                      <span className="text-xs font-mono text-gray-900">{r.roleId}</span>
                      <span className="text-[10px] text-muted">
                        granted {new Date(r.grantedAt).toLocaleDateString('en-GB')}
                      </span>
                    </div>
                    <button
                      type="button"
                      data-testid={`revoke-role-${r.roleId}`}
                      onClick={() => revokeMutation.mutate(r.roleId)}
                      disabled={revokeMutation.isPending}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-red-700 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="w-3 h-3" />
                      Revoke
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl p-5" data-testid="my-roles-panel">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-4 h-4 text-nipost-green" />
            <h3 className="text-sm font-semibold text-gray-900">My roles</h3>
          </div>
          <p className="text-xs text-muted mb-3">
            Roles currently granted to this account.
          </p>
          {userRoles.isPending ? (
            <p className="text-xs text-muted">Loading roles…</p>
          ) : userRoles.isError ? (
            <p className="text-xs text-danger">
              {(userRoles.error as Error).message}
            </p>
          ) : (userRoles.data?.length ?? 0) === 0 ? (
            <p className="text-xs text-muted" data-testid="no-roles">
              No roles granted.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {(userRoles.data ?? []).map((r) => (
                <li
                  key={r.roleId}
                  data-testid={`my-role-${r.roleId}`}
                  className="flex items-center gap-2"
                >
                  <ShieldCheck className="w-3 h-3 text-nipost-green" />
                  <span className="text-xs font-mono text-gray-900">{r.roleId}</span>
                  <span className="text-[10px] text-muted">
                    since {new Date(r.grantedAt).toLocaleDateString('en-GB')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
